import React, { useContext, useEffect, useState } from 'react'
import { ApiContext } from '../../App';
import { getTagColor, getColorForType, groupTagsByType } from '../../styles/tagUtils';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const ViewAllTags = () => {
    const [tags, setTags] = useState([]);
    const [tagTypes, setTagTypes] = useState([]);
    const [newTagName, setNewTag] = useState('');
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);
    const [selectedTypeIds, setSelectedTypeIds] = useState([]);
    const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);
    const [newTypeName, setNewTypeName] = useState('');
    const [showTypesListModal, setShowTypesListModal] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [editTypeName, setEditTypeName] = useState('');
    const [showDropdownMenu, setShowDropdownMenu] = useState(null);
    const [showExplicationModal, setShowExplicationModal] = useState(false);
    const [selectedTagForExplication, setSelectedTagForExplication] = useState(null);
    const [explicationText, setExplicationText] = useState('');
    const [hoveredTooltip, setHoveredTooltip] = useState(null);
    const [showDeleteTagDialog, setShowDeleteTagDialog] = useState(false);
    const [tagToDelete, setTagToDelete] = useState(null);
    const [showDeleteTypeDialog, setShowDeleteTypeDialog] = useState(false);
    const [typeToDelete, setTypeToDelete] = useState(null);
    const API = useContext(ApiContext);

    // Charger les tags avec leurs types
    const fetchTags = async () => {
        try {
            const response = await API.get('/api/tags');
            const tagsWithTypes = await Promise.all(
                response.data.map(async (tag) => {
                    if (tag.type && tag.type.length > 0) {
                        tag.type = tag.type.filter(type => type !== null);
                    }
                    return tag;
                })
            );
            setTags(tagsWithTypes);
        } catch (error) {
            setError('Erreur lors du chargement des tags');
            console.error('Erreur', error);
        }
    };

    // Charger les types de tags
    const fetchTagTypes = async () => {
        try {
            const response = await API.get('/api/tags/tag-types');
            setTagTypes(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des types:', error);
        }
    };

    useEffect(() => {
        fetchTags();
        fetchTagTypes();

        const handleClickOutside = (event) => {
            if (!event.target.closest('.relative')) {
                setShowDropdownMenu(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // Ajouter un nouveau tag
    const handleAddTag = async (e) => {
        e.preventDefault();
        if (!newTagName.trim()) return;

        try {
            await API.post('/api/tags', { name: newTagName.trim() });
            setNewTag('');
            fetchTags();
            setError('');
        } catch (error) {
            setError(error.response?.data?.message || 'Erreur lors de l\'ajout du tag');
            console.error('Erreur:', error);
        }
    };

    // Supprimer un tag
    const handleDeleteTag = async () => {
        if (!tagToDelete) return;

        const tagId = tagToDelete;
        setShowDeleteTagDialog(false);
        setTagToDelete(null);

        try {
            await API.delete(`/api/tags/${tagId}`);
            fetchTags();
            setError('');
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    // Ouvrir la modal pour assigner des types
    const openTypeModal = (tag) => {
        setSelectedTag(tag);
        setSelectedTypeIds(tag.type ? tag.type.map(t => t._id) : []);
        setShowModal(true);
        setShowDropdownMenu(null);
    };

    // Ouvrir la modal pour l'explication
    const openExplicationModal = (tag) => {
        setSelectedTagForExplication(tag);
        setExplicationText(tag.explication || '');
        setShowExplicationModal(true);
        setShowDropdownMenu(null);
    };

    // Sauvegarder l'explication
    const handleSaveExplication = async () => {
        try {
            await API.put(`/api/tags/${selectedTagForExplication._id}`, {
                name: selectedTagForExplication.name,
                explication: explicationText.trim()
            });
            setShowExplicationModal(false);
            setSelectedTagForExplication(null);
            setExplicationText('');
            fetchTags();
        } catch (error) {
            setError('Erreur lors de la sauvegarde de l\'explication');
            console.error('Erreur:', error);
        }
    };

    // Toggle du menu déroulant
    const toggleDropdownMenu = (tagId) => {
        setShowDropdownMenu(showDropdownMenu === tagId ? null : tagId);
    };

    // Assigner des types à un tag
    const handleAssignTypes = async () => {
        try {
            await API.put(`/api/tags/${selectedTag._id}/types`, {
                typeIds: selectedTypeIds
            });
            setShowModal(false);
            setSelectedTag(null);
            setSelectedTypeIds([]);
            fetchTags();
        } catch (error) {
            setError('Erreur lors de l\'assignation des types');
            console.error('Erreur:', error);
        }
    };

    // Créer un nouveau type de tag
    const handleCreateType = async (e) => {
        e.preventDefault();
        if (!newTypeName.trim()) return;

        try {
            await API.post('/api/tags/tag-types', { name: newTypeName.trim() });
            setNewTypeName('');
            setShowCreateTypeModal(false);
            fetchTagTypes();
            setError('');
        } catch (error) {
            setError(error.response?.data?.message || 'Erreur lors de la création du type');
            console.error('Erreur:', error);
        }
    };

    // Modifier un type
    const handleUpdateType = async (e) => {
        e.preventDefault();
        if (!editTypeName.trim()) return;

        try {
            await API.put(`/api/tags/tag-types/${editingType._id}`, { 
                name: editTypeName.trim() 
            });
            setEditingType(null);
            setEditTypeName('');
            fetchTagTypes();
            fetchTags();
            setError('');
        } catch (error) {
            setError(error.response?.data?.message || 'Erreur lors de la modification');
            console.error('Erreur:', error);
        }
    };

    // Supprimer un type
    const handleDeleteType = async () => {
        if (!typeToDelete) return;

        const typeId = typeToDelete;
        setShowDeleteTypeDialog(false);
        setTypeToDelete(null);

        try {
            await API.delete(`/api/tags/tag-types/${typeId}`);
            fetchTagTypes();
            fetchTags();
            setError('');
        } catch (error) {
            setError(error.response?.data?.message || 'Erreur lors de la suppression');
            console.error('Erreur:', error);
        }
    };

    // Commencer l'édition d'un type
    const startEditType = (type) => {
        setEditingType(type);
        setEditTypeName(type.name);
    };

    // Annuler l'édition
    const cancelEdit = () => {
        setEditingType(null);
        setEditTypeName('');
    };

    const handleTypeToggle = (typeId) => {
        setSelectedTypeIds(prev => 
            prev.includes(typeId) 
                ? prev.filter(id => id !== typeId)
                : [...prev, typeId]
        );
    };

    // Filtrer les tags selon la recherche
    const filteredTags = tags.filter(tag =>
        tag.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ✅ UTILISATION OPTIMISÉE DE tagUtils - Grouper les tags par type avec couleurs
    const groupedTagsWithColors = groupTagsByType(filteredTags);

    return (
        <div className='bg-white rounded-lg p-4 sm:p-6 border '>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
                <h2 className='text-lg sm:text-xl font-semibold m-0'>Gestionnaire de Tags</h2>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <button
                        onClick={() => setShowTypesListModal(true)}
                        className='px-3 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition text-xs sm:text-sm w-full sm:w-auto'
                    >
                        <i className="bi bi-list mr-1"></i>
                        <span className="hidden sm:inline">Gérer les Types</span>
                        <span className="sm:hidden">Types</span>
                    </button>
                    <button
                        onClick={() => setShowCreateTypeModal(true)}
                        className='px-3 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition text-xs sm:text-sm w-full sm:w-auto'
                    >
                        <i className="bi bi-plus mr-1"></i>
                        <span className="hidden sm:inline">Nouveau Type</span>
                        <span className="sm:hidden">Type</span>
                    </button>
                </div>
            </div>

            <div className='text-xs sm:text-sm text-gray-600 mb-4 space-y-1'>
                <li className='leading-relaxed'>
                    Clic sur les 3 points à côté d'un tag pour gérer ses types ou son explication.
                </li>
                <li className='leading-relaxed'>
                    Les types servent à catégoriser les tags et à leur attribuer des couleurs pour une meilleure visibilité.
                </li>
                <li className='leading-relaxed'>
                    L'explication est une note optionnelle visible au survol du tag.
                </li>
            </div>

            {/* Barre de recherche responsive */}
            <div className='mb-4 sm:mb-6'>
                <div className='w-full'>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder='Rechercher un tag...'
                        className='w-full p-2 sm:p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base'
                    />
                </div>
            </div>

            {error && <p className='text-red-500 mb-4'>{error}</p>}

            {/* ✅ AFFICHAGE OPTIMISÉ - Tags groupés par type avec couleurs */}
            <div className='space-y-6'>
                {Object.entries(groupedTagsWithColors).map(([typeName, typeData]) => (
                    <div key={typeName}>
                        {/* ✅ Titre de section avec couleur du type */}
                        <h3 
                            className='text-base sm:text-lg font-medium mb-2 sm:mb-3 border-b pb-1'
                        >
                            {typeName} ({typeData.tags.length})
                        </h3>
                        
                        <div className='flex flex-wrap gap-1 sm:gap-2'>
                            {typeData.tags.map((tag) => {
                                // ✅ Utilisation de getTagColor pour la cohérence                                
                                return (
                                    <div key={tag._id} className="relative">
                                        <div
                                            className="flex bg-indigo-50  border-indigo-200 text-indigo-700 items-center px-2 sm:px-3 py-1 rounded-full transition-all duration-200  hover:shadow-sm hover:bg-indigo-100"
                                            onMouseEnter={() => tag.explication && setHoveredTooltip(tag._id)}
                                            onMouseLeave={() => setHoveredTooltip(null)}
                                        >
                                            <span className='mr-1 sm:mr-2 text-xs sm:text-sm font-medium'>{tag.name}</span>
                                            
                                            {/* Menu 3 points avec dropdown */}
                                            <div className="relative">
                                                <button
                                                    onClick={() => toggleDropdownMenu(tag._id)}
                                                    className='hover:opacity-70 text-gray-700 transition-opacity duration-200 w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center rounded-full mr-1'
                                                    title='Options'
                                                >
                                                    <i className="bi bi-three-dots-vertical text-xs"></i>
                                                </button>

                                                {/* Menu déroulant */}
                                                {showDropdownMenu === tag._id && (
                                                    <div className="absolute top-full right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-36 sm:min-w-48">
                                                        <button
                                                            onClick={() => openTypeModal(tag)}
                                                            className="w-full text-left px-3 sm:px-4 py-2 hover:bg-gray-100 text-xs sm:text-sm text-slate-700 border-b"
                                                        >
                                                            <i className="bi bi-tags mr-1 sm:mr-2"></i>
                                                            <span className="hidden sm:inline">Attribuer un type</span>
                                                            <span className="sm:hidden">Type</span>
                                                        </button>
                                                        <button
                                                            onClick={() => openExplicationModal(tag)}
                                                            className="w-full text-left px-3 sm:px-4 py-2 hover:bg-gray-100 text-xs sm:text-sm text-slate-700"
                                                        >
                                                            <i className="bi bi-info-circle mr-1 sm:mr-2"></i>
                                                            <span className="hidden sm:inline">{tag.explication ? 'Modifier l\'explication' : 'Ajouter une explication'}</span>
                                                            <span className="sm:hidden">Info</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Bouton supprimer */}
                                            <button
                                                onClick={() => {
                                                    setTagToDelete(tag._id);
                                                    setShowDeleteTagDialog(true);
                                                }}
                                                className='hover:text-red-500 text-red-500 transition-colors duration-200 w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center rounded-full'
                                                title='Supprimer'
                                            >
                                                <i className="bi bi-x text-xs sm:text-sm"></i>
                                            </button>
                                        </div>

                                        {/* Tooltip personnalisé rapide pour l'explication */}
                                        {hoveredTooltip === tag._id && tag.explication && (
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white text-xs sm:text-sm rounded-lg opacity-100 transition-opacity duration-150 pointer-events-none max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg text-left z-40 shadow-lg">
                                                <div className="font-semibold mb-1 sm:mb-2 text-center border-b border-gray-600 pb-1 text-xs sm:text-sm">{tag.name}</div>
                                                <div className="text-xs text-gray-200 leading-relaxed">{tag.explication}</div>
                                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* ✅ MODAL POUR LES TYPES - Avec couleurs */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-md lg:max-w-lg max-h-[80vh] overflow-y-auto">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-2 sm:space-y-0">
                            <h3 className="text-base sm:text-lg font-semibold pr-4 m-0">
                                Gérer les types pour "{selectedTag?.name}"
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-slate-500 hover:text-slate-700 self-end sm:self-auto"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        <div className="space-y-3 mb-6">
                            {tagTypes.map((type) => {
                                // ✅ Couleur pour chaque type dans la modal
                                const typeColor = getColorForType(type.name);
                                
                                return (
                                    <label
                                        key={type._id}
                                        className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedTypeIds.includes(type._id)}
                                            onChange={() => handleTypeToggle(type._id)}
                                            className="rounded border-slate-300"
                                        />
                                        <div 
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: typeColor }}
                                        ></div>
                                        <span className="text-sm">{type.name}</span>
                                    </label>
                                );
                            })}
                            
                            <button
                                onClick={() => setShowCreateTypeModal(true)}
                                className="w-full mt-3 px-3 py-2 border-2 border-dashed border-slate-300 text-slate-600 rounded hover:border-slate-400 hover:text-slate-700 transition text-sm"
                            >
                                <i className="bi bi-plus mr-1"></i>
                                Créer un nouveau type
                            </button>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 border rounded hover:bg-gray-100 text-sm sm:text-base"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleAssignTypes}
                                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm sm:text-base"
                            >
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ MODAL LISTE DES TYPES - Avec couleurs */}
            {showTypesListModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-md lg:max-w-lg max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base sm:text-lg font-semibold m-0">Gérer les Types de Tags</h3>
                            <button
                                onClick={() => {
                                    setShowTypesListModal(false);
                                    cancelEdit();
                                    setError('');
                                }}
                                className="text-slate-500 hover:text-slate-700"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {error && <p className='text-red-500 text-sm mb-4'>{error}</p>}

                        <div className="space-y-3 mb-4">
                            {tagTypes.length === 0 ? (
                                <p className="text-slate-500 text-center py-4">Aucun type créé</p>
                            ) : (
                                tagTypes.map((type) => {
                                    // ✅ Couleur pour chaque type
                                    const typeColor = getColorForType(type.name);
                                    
                                    return (
                                        <div 
                                            key={type._id} 
                                            className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                                            style={{ borderLeftColor: typeColor, borderLeftWidth: '4px' }}
                                        >
                                            {editingType && editingType._id === type._id ? (
                                                <form onSubmit={handleUpdateType} className="flex-1 flex items-center space-x-2">
                                                    <input
                                                        type="text"
                                                        value={editTypeName}
                                                        onChange={(e) => setEditTypeName(e.target.value)}
                                                        className="flex-1 p-1 border rounded text-sm"
                                                        autoFocus
                                                    />
                                                    <button
                                                        type="submit"
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Sauvegarder"
                                                    >
                                                        <i className="bi bi-check-lg"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={cancelEdit}
                                                        className="text-slate-500 hover:text-slate-700"
                                                        title="Annuler"
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                </form>
                                            ) : (
                                                <>
                                                    <div className="flex items-center space-x-3">
                                                        <div 
                                                            className="w-3 h-3 rounded-full"
                                                            style={{ backgroundColor: typeColor }}
                                                        ></div>
                                                        <span className="text-sm font-medium">{type.name}</span>
                                                    </div>
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => startEditType(type)}
                                                            className="text-blue-600 hover:text-blue-800 p-1"
                                                            title="Modifier"
                                                        >
                                                            <i className="bi bi-pencil text-xs"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setTypeToDelete(type._id);
                                                                setShowDeleteTypeDialog(true);
                                                            }}
                                                            className="text-red-600 hover:text-red-800 p-1"
                                                            title="Supprimer"
                                                        >
                                                            <i className="bi bi-trash text-xs"></i>
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <button
                            onClick={() => {
                                setShowTypesListModal(false);
                                setShowCreateTypeModal(true);
                            }}
                            className="w-full px-3 py-2 border-2 border-dashed border-slate-300 text-slate-600 rounded hover:border-slate-400 hover:text-slate-700 transition text-sm"
                        >
                            <i className="bi bi-plus mr-1"></i>
                            Créer un nouveau type
                        </button>

                        <div className="flex justify-end mt-4">
                            <button
                                onClick={() => {
                                    setShowTypesListModal(false);
                                    cancelEdit();
                                    setError('');
                                }}
                                className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700 text-sm sm:text-base w-full sm:w-auto"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Autres modals inchangées... */}
            {showExplicationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm sm:max-w-md lg:max-w-lg">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-2 sm:space-y-0">
                            <h3 className="text-base sm:text-lg font-semibold pr-4 m-0">
                                {selectedTagForExplication?.explication ? 'Modifier' : 'Ajouter'} l'explication pour "{selectedTagForExplication?.name}"
                            </h3>
                            <button
                                onClick={() => {
                                    setShowExplicationModal(false);
                                    setSelectedTagForExplication(null);
                                    setExplicationText('');
                                    setError('');
                                }}
                                className="text-slate-500 hover:text-slate-700 self-end sm:self-auto"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Explication
                            </label>
                            <textarea
                                value={explicationText}
                                onChange={(e) => setExplicationText(e.target.value)}
                                placeholder="Décrivez ce que représente ce tag..."
                                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                rows="4"
                                autoFocus
                            />
                        </div>

                        {error && <p className='text-red-500 text-sm mb-4'>{error}</p>}

                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                            <button
                                onClick={() => {
                                    setShowExplicationModal(false);
                                    setSelectedTagForExplication(null);
                                    setExplicationText('');
                                    setError('');
                                }}
                                className="px-4 py-2 border rounded hover:bg-gray-100 text-sm sm:text-base"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSaveExplication}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm sm:text-base"
                            >
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCreateTypeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-xs sm:max-w-sm">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base sm:text-lg font-semibold m-0">Créer un nouveau type</h3>
                            <button
                                onClick={() => {
                                    setShowCreateTypeModal(false);
                                    setNewTypeName('');
                                    setError('');
                                }}
                                className="text-slate-500 hover:text-slate-700"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        <form onSubmit={handleCreateType}>
                            <div className="mb-4">
                                <input
                                    type="text"
                                    value={newTypeName}
                                    onChange={(e) => setNewTypeName(e.target.value)}
                                    placeholder="Nom du type (ex: secteur, ville...)"
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                    autoFocus
                                />
                            </div>

                            {error && <p className='text-red-500 text-sm mb-4'>{error}</p>}

                            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateTypeModal(false);
                                        setNewTypeName('');
                                        setError('');
                                    }}
                                    className="px-4 py-2 border rounded hover:bg-gray-100 text-sm sm:text-base"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm sm:text-base"
                                >
                                    Créer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Message si aucun tag */}
            {filteredTags.length === 0 && (
                <p className="text-slate-500 text-center py-8">
                    {searchTerm ? 'Aucun tag trouvé pour cette recherche' : 'Aucun tag disponible'}
                </p>
            )}

            {/* Confirmation Dialog for Tag Delete */}
            <ConfirmationDialog
                isOpen={showDeleteTagDialog}
                onClose={() => {
                    setShowDeleteTagDialog(false);
                    setTagToDelete(null);
                }}
                onConfirm={handleDeleteTag}
                title="Supprimer le tag"
                message="Êtes-vous sûr de vouloir supprimer ce tag ? Cette action est irréversible."
                confirmText="Supprimer"
                cancelText="Annuler"
                isDangerous={true}
            />

            {/* Confirmation Dialog for Type Delete */}
            <ConfirmationDialog
                isOpen={showDeleteTypeDialog}
                onClose={() => {
                    setShowDeleteTypeDialog(false);
                    setTypeToDelete(null);
                }}
                onConfirm={handleDeleteType}
                title="Supprimer le type"
                message="Êtes-vous sûr de vouloir supprimer ce type ? Cette action est irréversible."
                confirmText="Supprimer"
                cancelText="Annuler"
                isDangerous={true}
            />
        </div>
    )
}

export default ViewAllTags